import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight, Users, Globe, Building2, Handshake, Zap,
  Award, Network, Send, Loader2, Check, ChevronRight,
  Share2, Megaphone, CalendarDays
} from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import ShareButtons from '@/components/ShareButtons';

const API = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  {
    id: 'referral',
    name: 'Referral Partner',
    tagline: 'Recommend. Earn. Grow together.',
    icon: Users,
    color: '#06b6d4',
    description: 'Introduce Taxera to your network of enterprise clients. When a referral converts, you earn a fee — no implementation work required on your side.',
    ideal: 'Tax advisors, management consultants, industry associations',
    benefits: ['Referral commission on closed deals', 'Co-branded collateral', 'Dedicated partner manager', 'Quarterly pipeline reviews'],
  },
  {
    id: 'reseller',
    name: 'Reseller',
    tagline: 'Sell Taxera as part of your portfolio.',
    icon: Globe,
    color: '#8b5cf6',
    description: 'Add Taxera to your solution portfolio with full commercial terms. You own the client relationship — we provide the platform, training, and sales enablement.',
    ideal: 'VAT consultancies, managed service providers, regional tax firms',
    benefits: ['Reseller margins on license & services', 'Full sales enablement & training', 'Lead sharing & co-marketing', 'Priority product roadmap input'],
  },
  {
    id: 'system-integrator',
    name: 'System Integrator',
    tagline: 'Implement. Certify. Deliver.',
    icon: Network,
    color: '#0066CC',
    description: 'Implement Taxera for your enterprise clients. Get access to technical certification, implementation playbooks, and dedicated partner engineering support.',
    ideal: 'Big 4, SAP consultancies, global SI firms, boutique ERP practices',
    benefits: ['Technical certification programme', 'Implementation playbooks & methodology', 'Dedicated partner engineering support', 'Joint go-to-market campaigns'],
  },
  {
    id: 'strategic',
    name: 'Strategic Alliance',
    tagline: 'Shape the future of tax technology.',
    icon: Award,
    color: '#ec4899',
    description: 'Deep, long-term alliance with executive alignment. Joint go-to-market, co-development opportunities, and coordinated enterprise pursuits.',
    ideal: 'Global technology firms, platform vendors, industry leaders',
    benefits: ['Executive sponsor alignment', 'Co-development & roadmap influence', 'Joint enterprise pursuits', 'Shared thought leadership & events'],
  },
];

const PartnersPage = () => {
  const location = useLocation();
  const [announcements, setAnnouncements] = useState([]);
  const [formData, setFormData] = useState({
    company_name: '', contact_name: '', email: '', phone: '',
    tier: '', regions: '', sap_practice: false, message: '',
  });
  const [formStatus, setFormStatus] = useState('idle');
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/partners/announcements`)
      .then(r => r.json())
      .then(data => setAnnouncements(data.announcements || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 150);
    }
  }, [location.hash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('loading');
    try {
      const res = await fetch(`${API}/api/partners/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ─── Hero ─── */}
      <section className="relative bg-slate-900 overflow-hidden" data-testid="partners-hero">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(20,184,166,0.12)_0%,transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_30%,rgba(0,102,204,0.08)_0%,transparent_50%)]" />
        <div className="relative max-w-6xl mx-auto px-6 pt-32 pb-20 lg:pt-40 lg:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-taxera-teal/10 border border-taxera-teal/20 text-taxera-teal text-xs font-bold uppercase tracking-[0.15em] rounded-full mb-6">
              <Handshake className="w-3.5 h-3.5" strokeWidth={2} />
              Partner Programme
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-5">
              Build your tax compliance
              <br />
              <span className="taxera-gradient-text">practice on Taxera</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mb-8">
              Whether you advise, resell, or implement — Taxera gives you a single enterprise platform to grow your indirect tax business. Fast deployments. One integration. Every country.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#become-a-partner"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-2xl transition-all hover:scale-105"
                data-testid="partners-apply-cta"
              >
                Become a Partner
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/media"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white rounded-full font-semibold hover:bg-white/20 transition-all border border-white/20"
                data-testid="partners-announcements-cta"
              >
                <Megaphone className="w-4 h-4" />
                Partner Announcements
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/5 text-white/70 rounded-full font-semibold hover:bg-white/10 hover:text-white transition-all border border-white/10"
                data-testid="partners-contact-cta"
              >
                Talk to our team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Why partner with Taxera ─── */}
      <section id="programme" className="py-16 lg:py-20 bg-white" data-testid="partners-why">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-taxera-teal mb-3">Why Taxera</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-taxera-charcoal mb-3">
              Why partners choose Taxera
            </h2>
            <p className="text-base text-slate-500 max-w-lg mx-auto">
              One platform. Pre-built country mandates. Middleware-native ERP integration. Your clients deploy in weeks, not months.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Fast time-to-revenue', desc: 'Pre-built deployments mean your clients go live in weeks. Faster implementations = faster billing for your practice.' },
              { icon: Building2, title: 'Enterprise platform', desc: 'One vendor, one database, one integration across e-Invoicing, SAF-T, VAT returns, and more. No multi-vendor fragmentation.' },
              { icon: Handshake, title: 'True partnership', desc: 'Dedicated partner managers, joint pipeline reviews, co-marketing investment, and a product roadmap influenced by partner feedback.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="rounded-2xl border border-slate-200 p-6 hover:border-taxera-teal/30 transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-taxera-teal/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-taxera-teal" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Partner Tiers ─── */}
      <section className="py-16 lg:py-20 bg-slate-50" id="partner-tiers" data-testid="partner-tiers">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-taxera-teal mb-3">Partnership Models</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-taxera-charcoal mb-3">
              Find the right partnership for you
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {TIERS.map((tier, i) => {
              const Icon = tier.icon;
              const isSelected = selectedTier === tier.id;
              return (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className={`group rounded-2xl border-2 bg-white p-6 lg:p-7 cursor-pointer transition-all duration-300 ${
                    isSelected ? 'border-taxera-teal shadow-lg' : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                  }`}
                  onClick={() => setSelectedTier(isSelected ? null : tier.id)}
                  data-testid={`tier-${tier.id}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${tier.color}15` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: tier.color }} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-taxera-charcoal">{tier.name}</h3>
                      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: tier.color }}>{tier.tagline}</p>
                    </div>
                    <ChevronRight
                      className={`w-5 h-5 text-slate-300 transition-transform duration-300 ${isSelected ? 'rotate-90' : ''}`}
                    />
                  </div>

                  <p className="text-sm text-slate-600 leading-relaxed mb-3">{tier.description}</p>
                  <p className="text-xs text-slate-400 mb-1"><span className="font-semibold text-slate-500">Ideal for:</span> {tier.ideal}</p>

                  <AnimatePresence>
                    {isSelected && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <p className="text-xs font-bold uppercase tracking-wide text-taxera-teal mb-3">What you get</p>
                          <ul className="space-y-2">
                            {tier.benefits.map((b, j) => (
                              <li key={`benefit-${b.slice(0, 30)}`} className="flex items-start gap-2.5 text-sm text-slate-600">
                                <Check className="w-4 h-4 text-taxera-teal shrink-0 mt-0.5" strokeWidth={2} />
                                {b}
                              </li>
                            ))}
                          </ul>
                          <a
                            href="#become-a-partner"
                            className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-taxera-blue hover:underline"
                            onClick={(e) => { e.stopPropagation(); }}
                          >
                            Apply as {tier.name} <ArrowRight className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Partner Announcements ─── */}
      <section className="py-16 lg:py-20 bg-white" id="announcements" data-testid="partner-announcements">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-taxera-teal mb-3">Newsroom</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-taxera-charcoal">
              Partner Announcements
            </h2>
          </motion.div>

          {announcements.length > 0 ? (
            <div className="space-y-8">
              {announcements.map((ann, i) => (
                <motion.article
                  key={ann.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg transition-all"
                  data-testid={`announcement-${ann.id}`}
                >
                  {/* Hero image */}
                  {ann.image_url && (
                    <div className="relative h-56 lg:h-72 overflow-hidden">
                      <img
                        src={ann.image_url}
                        alt={ann.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      {ann.tier && (
                        <span className="absolute top-4 left-4 text-2xs font-bold uppercase tracking-wide text-white bg-taxera-blue/90 backdrop-blur-sm px-3 py-1.5 rounded-full">
                          {ann.tier}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="p-6 lg:p-8">
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1">
                        {!ann.image_url && ann.tier && (
                          <span className="inline-block text-2xs font-bold uppercase tracking-wide text-white bg-taxera-blue px-2.5 py-1 rounded-full mb-3">
                            {ann.tier}
                          </span>
                        )}
                        {ann.published_at && (
                          <p className="text-xs text-slate-400 flex items-center gap-1.5 mb-3">
                            <CalendarDays className="w-3.5 h-3.5" />
                            {new Date(ann.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                        <h3 className="font-heading text-xl lg:text-2xl font-bold text-taxera-charcoal leading-tight mb-2">
                          {ann.title}
                        </h3>
                        {ann.partner_name && (
                          <p className="text-sm text-taxera-teal font-semibold">
                            with {ann.partner_name}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0">
                        <ShareButtons
                          url={`${window.location.origin}/partners`}
                          title={ann.title}
                          summary={ann.summary}
                          compact
                        />
                      </div>
                    </div>

                    {/* Summary */}
                    <p className="text-base text-slate-600 leading-relaxed mb-5">{ann.summary}</p>

                    {/* Full content with quote styling */}
                    {ann.content && (
                      <div className="text-sm text-slate-500 leading-relaxed space-y-4 mb-5">
                        {ann.content.split('\n\n').map((para, j) => {
                          if (para.startsWith('\u201C') || para.startsWith('"')) {
                            const dashIdx = para.lastIndexOf('\u2014');
                            const quoteText = dashIdx > 0 ? para.substring(0, dashIdx).trim() : para;
                            const attribution = dashIdx > 0 ? para.substring(dashIdx) : '';
                            return (
                              <blockquote key={`q-${para.slice(0, 30)}`} className="border-l-3 border-taxera-teal pl-5 py-3 bg-taxera-teal/[0.03] rounded-r-xl">
                                <p className="text-slate-600 italic leading-relaxed">{quoteText}</p>
                                {attribution && (
                                  <p className="text-xs font-semibold text-taxera-charcoal mt-2 not-italic">{attribution}</p>
                                )}
                              </blockquote>
                            );
                          }
                          return <p key={`p-${para.slice(0, 30)}`}>{para}</p>;
                        })}
                      </div>
                    )}

                    {/* Footer: announced by */}
                    <div className="flex items-center justify-between pt-5 border-t border-slate-100">
                      <div />
                      {ann.announced_by && (
                        <p className="text-xs text-slate-400">
                          <span className="text-slate-500 font-medium">{ann.announced_by}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Partner announcements coming soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* ─── Become a Partner Form ─── */}
      <section className="py-16 lg:py-20 bg-slate-900" id="become-a-partner" data-testid="partner-form-section">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-taxera-teal mb-3">Get Started</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">
              Become a Partner
            </h2>
            <p className="text-base text-slate-400 max-w-lg mx-auto">
              Tell us about your firm and the partnership you're interested in. We'll be in touch within 48 hours.
            </p>
          </motion.div>

          {formStatus === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-10 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-taxera-teal/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-taxera-teal" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-2">Application Received</h3>
              <p className="text-slate-400">Our partnerships team will review your application and reach out within 48 hours.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white/[0.06] backdrop-blur-sm rounded-2xl border border-white/10 p-6 lg:p-8 space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Company Name *</label>
                  <input
                    type="text" required
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 rounded-xl border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-taxera-teal/40 focus:border-taxera-teal/40 transition-all"
                    placeholder="Acme Consulting GmbH"
                    data-testid="partner-company-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Contact Name *</label>
                  <input
                    type="text" required
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 rounded-xl border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-taxera-teal/40 focus:border-taxera-teal/40 transition-all"
                    placeholder="Jane Doe"
                    data-testid="partner-contact-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Work Email *</label>
                  <input
                    type="email" required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 rounded-xl border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-taxera-teal/40 focus:border-taxera-teal/40 transition-all"
                    placeholder="jane@acme.com"
                    data-testid="partner-email-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 rounded-xl border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-taxera-teal/40 focus:border-taxera-teal/40 transition-all"
                    placeholder="+41 XX XXX XX XX"
                    data-testid="partner-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">Partnership Tier *</label>
                <select
                  required
                  value={formData.tier}
                  onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 rounded-xl border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-taxera-teal/40 focus:border-taxera-teal/40 transition-all appearance-none"
                  data-testid="partner-tier-select"
                >
                  <option value="" className="text-slate-900">Select a tier...</option>
                  <option value="referral" className="text-slate-900">Referral Partner</option>
                  <option value="reseller" className="text-slate-900">Reseller</option>
                  <option value="system-integrator" className="text-slate-900">System Integrator</option>
                  <option value="strategic" className="text-slate-900">Strategic Alliance</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Regions of Interest</label>
                  <input
                    type="text"
                    value={formData.regions}
                    onChange={(e) => setFormData({ ...formData, regions: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 rounded-xl border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-taxera-teal/40 focus:border-taxera-teal/40 transition-all"
                    placeholder="e.g., DACH, Nordics, LATAM"
                    data-testid="partner-regions-input"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 cursor-pointer py-3">
                    <input
                      type="checkbox"
                      checked={formData.sap_practice}
                      onChange={(e) => setFormData({ ...formData, sap_practice: e.target.checked })}
                      className="w-5 h-5 rounded border-white/20 bg-white/10 text-taxera-teal focus:ring-taxera-teal/40"
                      data-testid="partner-sap-checkbox"
                    />
                    <span className="text-sm text-slate-300">We have an existing SAP practice</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">Tell us more about your interest</label>
                <textarea
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 rounded-xl border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-taxera-teal/40 focus:border-taxera-teal/40 transition-all resize-none"
                  placeholder="What's driving your interest in partnering with Taxera? Which client segments do you serve?"
                  data-testid="partner-message-input"
                />
              </div>

              {formStatus === 'error' && (
                <p className="text-sm text-red-400">Something went wrong. Please try again.</p>
              )}

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-4 taxera-gradient text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                data-testid="partner-submit-btn"
              >
                {formStatus === 'loading' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Submit Application
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnersPage;
